import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { HeaderTitle } from '@rossum/components/header/Header';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import {
  Box,
  Button,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@rossum/ui/material';
import { ExtensionsHeader } from '../../components/ExtensionsHeader';
import { extensionDetailPath } from '../../routes';
import { RossumStoreTile } from '../store/RossumStoreTile';
import {
  CustomExtensionDrawer,
  CustomExtensionDrawerState,
} from './components/CustomExtensionDrawer';
import { useCreateStoreHook } from './hooks/useCreateStoreHook';
import { useExternalHookTemplates } from './hooks/useExternalHookTemplates';

export const ExternalStorePage = () => {
  const { mutate } = useCreateStoreHook();
  const { push } = useHistory();

  const [selectedHookTemplate, setSelectedHookTemplate] = useState<
    CustomExtensionDrawerState | undefined
  >(undefined);

  const { data: hookTemplates, isLoading } = useExternalHookTemplates();
  const intl = useIntl();

  return (
    <PageLayout
      fullWidth
      renderHeader={params => (
        <ExtensionsHeader
          {...params}
          value="externalStore"
          title={hasScrolled => (
            <Stack direction="row" alignItems="center" gap={1}>
              <HeaderTitle
                hasScrolled={hasScrolled}
                title={intl.formatMessage({
                  id: 'components.appBar.menu.extensions.externalStore',
                })}
              />
            </Stack>
          )}
        />
      )}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{
          maxWidth: 1400,
          pt: 4,
          mx: 'auto',
        }}
      >
        <Typography>
          {intl.formatMessage({
            id: 'features.externalStore.createStore.text',
          })}
        </Typography>
        <Button
          onClick={() =>
            mutate(undefined, {
              onSuccess: hook => {
                push(extensionDetailPath(hook.id));
              },
            })
          }
        >
          {intl.formatMessage({
            id: 'features.externalStore.createStore.link',
          })}
        </Button>
      </Stack>

      <Box sx={{ width: 1 }}>
        <Grid
          container
          spacing={2}
          sx={{
            maxWidth: 1400,
            p: 5,
            mx: 'auto',
          }}
        >
          {isLoading
            ? [0, 1, 2, 3, 4, 5].map(index => (
                <Grid key={index} size={4}>
                  <Skeleton width="100%" height={280} variant="rounded" />
                </Grid>
              ))
            : hookTemplates
              ? hookTemplates.map(
                  ({ externalHookTemplateId, hookInterface, hookTemplate }) => {
                    return (
                      <Grid key={hookTemplate.name} size={4}>
                        <RossumStoreTile
                          type="function"
                          runtime="nodejs22.x"
                          handleClick={() => {
                            if (hookInterface)
                              setSelectedHookTemplate({
                                hookInterfaceId: hookInterface.id,
                                externalHookTemplateId,
                              });
                          }}
                          name={hookTemplate.name}
                          description={
                            <Stack>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {hookInterface?.name}
                              </Typography>
                              <Typography variant="body2">
                                {hookTemplate.description}
                              </Typography>
                            </Stack>
                          }
                          imageUrl={hookTemplate.extension_image_url}
                        />
                      </Grid>
                    );
                  }
                )
              : null}
          <CustomExtensionDrawer
            selectedHookTemplate={selectedHookTemplate}
            hookTemplate={
              hookTemplates.find(
                tpl =>
                  tpl.externalHookTemplateId ===
                  selectedHookTemplate?.externalHookTemplateId
              )?.hookTemplate
            }
            onClose={() => setSelectedHookTemplate(undefined)}
          />
        </Grid>
      </Box>
    </PageLayout>
  );
};
